import React, { ChangeEvent, useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import Spacing from 'components/Spacing';
import PageSection from 'components/PageSection';
import useCompany from 'hooks/data/company/useCompany';
import PageContainer from 'components/PageContainer';
import { Alert, CircularProgress, Grid, Typography } from '@mui/material';
import { useForm, useFieldArray } from 'react-hook-form';
import api, { ApiResponses } from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { sumBy } from 'lodash';
import CompanyDetails from './CompanyDetails';
import FundSection from './FundSection';
import { Row } from 'components/Layout';
import { FundAllocation } from '../index';
import { useDisclosure } from 'further-ui/hooks';
import AlertDialog from 'components/AlertDialog';
import downloadAllocationFile from './download-allocation-file';
import { useUploadAllocations } from 'hooks/data/allocation/useUploadAllocations';
import { Api } from 'further-types/allocations';
import ErrorTable from '../ErrorsTable';
import { handleUploadAmendments } from './handle-upload-amendments';
import ActionsRow from './ActionsRow';
import { ALLOCATION_HISTORY } from 'adminConstants/routes';
import GridContainer from 'components/GridContainer';

// Form types
type FormValues = {
  fundAllocations: Array<FundAllocation>; // Include all original fields
};

const ReviewSection: React.FC<{
  fundAllocations: Array<FundAllocation>;
  companyId: string;
  setShareAllocationUploadId: (id: string) => void;
  onAllocationSuccess: () => void;
  onReprocessAllocation: () => void;
  isAutoAllocation: boolean;
  setFundAutoAllocationResults: (
    fundAllocations: Array<FundAllocation>,
  ) => void;
  existingAllocationId?: string;
  draftAllocationId?: string;
  containsExitedShareholdings: boolean;
  fundIdsInDraftAllocation: Array<string>;
  containsExportedTransactionFees: boolean;
  isShareConsiderationAllocation?: boolean;
  exitedCompanyInfo?: {
    tradingName: string;
    shareClass: string;
    exitId: string;
    exitAmendmentId?: string;
  } | null;
  setIsAutoAllocation: (isAutoAllocation: boolean) => void;
  refetchAllocation: () => void;
}> = ({
  fundAllocations = [],
  companyId,
  setShareAllocationUploadId,
  onAllocationSuccess,
  onReprocessAllocation,
  isAutoAllocation,
  setFundAutoAllocationResults,
  existingAllocationId,
  draftAllocationId,
  containsExitedShareholdings,
  fundIdsInDraftAllocation,
  containsExportedTransactionFees,
  isShareConsiderationAllocation = false,
  exitedCompanyInfo = null,
  setIsAutoAllocation,
  refetchAllocation,
}) => {
  const history = useHistory();

  const [amendmentsMenuAnchorEl, setAmendmentsMenuAnchorEl] = useState(null);
  const [amendmentsFile, setAmendmentsFile] = useState<File | null>(null);
  const [uploadErrors, setUploadErrors] = useState<Array<Api.RowError>>([]);
  const [isEditing, setIsEditing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [validationResult, setValidationResult] = useState<{
    isValid: boolean;
    isOverAllocated: boolean;
    isUnderAllocated: boolean;
  } | null>(null);
  const [updatedFundIds, setUpdatedFundIds] = useState<Set<string>>(new Set());

  // Store the latest AA results while ignoring amendments so we can go back a step if needed
  const [
    mostRecentAutoAllocationDataSnapshot,
    setMostRecentAutoAllocationDataSnapshot,
  ] = useState<Array<FundAllocation>>([]);

  const uploadWarningDisclosure = useDisclosure();
  const dismissEditDisclosure = useDisclosure();
  const allocationValidationDisclosure = useDisclosure();

  const handleReprocessAllocation = () => {
    if (mostRecentAutoAllocationDataSnapshot.length > 0) {
      setFundAutoAllocationResults(mostRecentAutoAllocationDataSnapshot);
    }

    onReprocessAllocation();
  };

  // Set up form with default values from fundAllocations
  const formMethods = useForm<FormValues>({
    defaultValues: {
      fundAllocations,
    },
  });

  const { control, handleSubmit, setValue, watch } = formMethods;

  // Set up field array for fundAllocations
  const { fields: formFundAllocations, replace } = useFieldArray({
    control,
    name: 'fundAllocations',
  });

  // Get current form values
  const currentFormValues = watch('fundAllocations');

  const { uploadFile, exportToCustodian } = useUploadAllocations();

  // Update form when fundAllocations prop changes (e.g., after refetch)
  // Only update when not editing to avoid disrupting user input
  useEffect(() => {
    if (fundAllocations && fundAllocations.length > 0 && !isEditing) {
      replace(fundAllocations);
      setUpdatedFundIds(new Set()); // Reset tracking when form is reset
    }
  }, [fundAllocations, replace, isEditing]);

  // Process amendments file when it changes
  useEffect(() => {
    handleUploadAmendments({
      amendmentsFile,
      setUploadErrors,
      uploadFile,
      isAllocationPreviewAutoAllocated,
      formFundAllocations,
      setMostRecentAutoAllocationDataSnapshot,
      replace,
      setIsAutoAllocation,
      setIsEditing,
    });
  }, [amendmentsFile]);

  const isAllocationPreviewAutoAllocated =
    currentFormValues[0]?.isAutoAllocation;

  const { data: investee } = useCompany(fundAllocations[0]?.companyId ?? '');

  const handleMenuOpen = (event) => {
    setAmendmentsMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAmendmentsMenuAnchorEl(null);
  };

  const onFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAmendmentsFile(e.target.files?.[0] ?? null);
  };

  const handleReupload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const createShareAllocationsMutation =
    api.eisAllocations.createShareAllocations.useMutation(
      useHandleTrpcMutation<
        ApiResponses['eisAllocations']['createShareAllocations']
      >()({
        successMessage: 'Share allocations created successfully.',
        onSuccess: (data) => {
          const { shareAllocationUploadId } = data;
          setShareAllocationUploadId(shareAllocationUploadId);

          // Mark any fund allocation with finalDeal: true as isFullyDeployed: true
          currentFormValues.forEach((fundAllocation, index) => {
            if (fundAllocation.finalDeal) {
              setValue(`fundAllocations.${index}.isFullyDeployed`, true);
            }
          });

          onAllocationSuccess();
        },
      }),
    );

  const editShareAllocationsMutation =
    api.eisAllocations.editShareAllocations.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Allocation edit saved successfully.',
        onSuccess: () => {
          setIsEditing(false);
          setAmendmentsFile(null); // Clear amendments file when successfully saved
          setUploadErrors([]); // Clear any upload errors
          setUpdatedFundIds(new Set()); // Clear tracked fund changes
          refetchAllocation(); // Refetch fresh data from backend
          // Mark any fund allocation with finalDeal: true as isFullyDeployed: true
          currentFormValues.forEach((fundAllocation, index) => {
            if (fundAllocation.finalDeal) {
              setValue(`fundAllocations.${index}.isFullyDeployed`, true);
            }
          });
        },
      }),
    );

  const createDraftAllocationMutation =
    api.eisAllocations.createDraftAllocation.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Draft allocation saved successfully.',
        onSuccess: () => {
          history.push(ALLOCATION_HISTORY);
        },
      }),
    );

  const formatFundAllocationsToSubmissionData = (
    formFundAllocations: Array<FundAllocation>,
  ) => {
    return formFundAllocations.map((fund, fundIndex) => {
      // Use original fund data for sharesAllocated calculation - from the component prop
      const originalFund = fundAllocations[fundIndex];
      return {
        trancheId: fund.trancheId,
        companyId: companyId || fund.companyId,
        allocationDate: fund.allocationDate,
        dealSharePrice: fund.dealSharePrice,
        fundName: fund.trancheName,
        finalDeal: fund.finalDeal,
        minimumInvestorPortfolioSize: fund.minimumInvestorPortfolioSize ?? 0,
        maximumSingleCompanyAllocation:
          fund.maximumSingleCompanyAllocation ?? 0,
        isAutoAllocation: fund.isAutoAllocation,
        targetShares: fund.targetShares,
        sharesAllocated: originalFund.totalSharesAllocated,
        transactionFeePercent: fund.transactionFeePercent ?? 0,
        defaultTransactionFeeVatPercent:
          fund.defaultTransactionFeeVatPercent ?? 0,
        investments: fund.investments.map((investment) => ({
          investmentId: investment.investmentId,
          allocatedShares: investment.finalShareholding,
          subscriptionAmount: investment.adjustedNetInvestmentExFee,
          transactionFee: investment.transactionFeeAmount,
          transactionFeeNet: investment.transactionFeeNet,
          transactionFeeVat: investment.transactionFeeVat,
          investorName: investment.investor?.fullName ?? '',
          defaultTransactionFeeVatPercent:
            investment.defaultTransactionFeeVatPercent,
        })),
      };
    });
  };

  // Validation function for allocation checks
  const validateAllocation = (
    formData: Array<FundAllocation>,
    isNewAllocation: boolean,
  ) => {
    for (let i = 0; i < formData.length; i++) {
      const currentFund = formData[i];
      const originalFund = fundAllocations[i];

      // Calculate current total shares allocated from form data
      const currentTotalSharesAllocated = sumBy(
        currentFund.investments,
        (investment) => investment.finalShareholding ?? 0,
      );

      // For new allocations, compare against target shares
      // For existing allocations, compare against original total shares
      const targetTotalShares = isNewAllocation
        ? originalFund.targetShares
        : sumBy(
            originalFund.investments,
            (investment) => investment.finalShareholding ?? 0,
          );

      if (currentTotalSharesAllocated !== targetTotalShares) {
        return {
          isValid: false,
          isOverAllocated: currentTotalSharesAllocated > targetTotalShares,
          isUnderAllocated: currentTotalSharesAllocated < targetTotalShares,
        };
      }
    }
    return { isValid: true, isOverAllocated: false, isUnderAllocated: false };
  };

  const getValidationMessage = () => {
    const isExistingAllocation = !!existingAllocationId;
    const comparisonPhrase = isExistingAllocation
      ? 'were originally allocated'
      : 'the target allocation';

    const comparison = validationResult?.isOverAllocated ? 'more' : 'fewer';

    return `There are currently ${comparison} shares allocated than ${comparisonPhrase}. Do you wish to continue anyway?`;
  };

  // Handle form submission
  const onSubmit = (data: FormValues) => {
    const submissionData = formatFundAllocationsToSubmissionData(
      data.fundAllocations,
    );
    if (isEditing && existingAllocationId) {
      const updatedFundIdsArray = Array.from(updatedFundIds);

      editShareAllocationsMutation.mutate({
        fundAllocations: submissionData,
        shareAllocationUploadId: existingAllocationId,
        updatedFundIds: updatedFundIdsArray,
      });
    } else {
      createShareAllocationsMutation.mutate({
        fundAllocations: submissionData,
      });
    }
  };

  // Handle validation and submission
  const handleValidationAndSubmit = (data: FormValues) => {
    const validation = existingAllocationId
      ? validateAllocation(data.fundAllocations, false)
      : validateAllocation(data.fundAllocations, true);

    if (validation.isValid) {
      onSubmit(data);
    } else {
      setValidationResult(validation);
      allocationValidationDisclosure.onOpen();
    }
  };

  // Handle save edit button click
  const handleSaveEdit = () => {
    if (!existingAllocationId) {
      // If no existing allocation, preserve the edited values and exit edit mode
      setFundAutoAllocationResults(currentFormValues);
      setIsEditing(false);
      setAmendmentsFile(null); // Clear amendments file when exiting edit mode
    } else {
      // If existing allocation, validate and submit the edit
      handleSubmit(handleValidationAndSubmit)();
    }
  };

  // Handle submit button click
  const handleSubmitClick = () => {
    if (isEditing && !existingAllocationId) {
      // Preserve the edited values before exiting edit mode
      setFundAutoAllocationResults(currentFormValues);
      setIsEditing(false);
      setAmendmentsFile(null); // Clear amendments file when exiting edit mode
    } else {
      handleSubmit(handleValidationAndSubmit)();
    }
  };

  const onDraftSubmit = (data: FormValues) => {
    // Clear edit state if draft saved
    setIsEditing(false);
    setAmendmentsFile(null); // Clear amendments file when saving draft
    const submissionData = formatFundAllocationsToSubmissionData(
      data.fundAllocations,
    );
    createDraftAllocationMutation.mutate({ fundAllocations: submissionData });
  };

  const handleDownloadAllocation = () => {
    const companyTradingName = investee?.tradingName ?? '';
    const allocationDate = fundAllocations[0]?.allocationDate;
    exportToCustodian.mutate({
      shareAllocationUploadId: existingAllocationId ?? '',
      companyTradingName,
      allocationDate: allocationDate.toUTCDate(),
    });
  };

  // Mark a fund as modified when its data changes
  const markFundAsModified = (fundIndex: number) => {
    const fundId = formFundAllocations[fundIndex]?.trancheId;
    if (fundId && !updatedFundIds.has(fundId)) {
      setUpdatedFundIds((prev) => new Set(prev).add(fundId));
    }
  };

  const handleDismissEdit = () => {
    // Reset form to original fundAllocations
    formMethods.reset({
      fundAllocations,
    });
    setIsEditing(false);
    setUploadErrors([]);
    setAmendmentsFile(null); // Clear amendments file when leaving edit mode
    setUpdatedFundIds(new Set()); // Clear tracked fund changes
    // Restore original isAutoAllocation state
    const originalIsAutoAllocation =
      fundAllocations[0]?.isAutoAllocation ?? false;
    setIsAutoAllocation(originalIsAutoAllocation);
    dismissEditDisclosure.onClose();
  };

  const anyInvestorsOverAllocated = currentFormValues.some(
    (fundAllocation, fundIndex) =>
      fundAllocation.investments.some((investment, investmentIndex) => {
        const originalInvestment =
          fundAllocations[fundIndex]?.investments?.[investmentIndex];
        return (
          (investment.adjustedNetInvestmentExFee ?? 0) +
            (investment.transactionFeeAmount ?? 0) >
          (existingAllocationId
            ? (investment.heldByFundAwaitingInvestment ?? 0) +
              (originalInvestment?.adjustedNetInvestmentExFee ?? 0) +
              (originalInvestment?.transactionFeeAmount ?? 0)
            : Math.min(
                investment.investmentCapitalRemaining ?? 0,
                investment.heldByFundAwaitingInvestment ?? 0,
              ))
        );
      }),
  );

  const hasErrorInAllocation = currentFormValues.some((fundAllocation) => {
    // No errors for uploaded sheets or existing allocations in View
    if (
      !isAllocationPreviewAutoAllocated ||
      existingAllocationId ||
      draftAllocationId
    ) {
      return false;
    }

    const allSharesAllocated =
      fundAllocation.totalSharesAllocated >= fundAllocation.targetShares;

    const allInvestorsAllocated =
      fundAllocation.investmentsAllocatedTo >=
      fundAllocation.investmentsInTranche;

    return (
      !allSharesAllocated || !allInvestorsAllocated || anyInvestorsOverAllocated
    );
  });

  const hasAnyInvestorsAllocated = currentFormValues.some(
    (fundAllocation) => fundAllocation.investmentsAllocatedTo > 0,
  );

  const disabledSubmitTooltip = existingAllocationId
    ? undefined
    : anyInvestorsOverAllocated
    ? 'This allocation contains investors with insufficient capital awaiting investment or requires unpaid committed capital to be paid by some investors prior to investment.'
    : !hasAnyInvestorsAllocated
    ? 'No investors will receive shares in this allocation. Please review the allocation and ensure that all investors have been allocated.'
    : undefined;

  return (
    <PageContainer heading="Allocation summary">
      <Spacing size="lg">
        {!uploadErrors.length && !uploadFile.isPending && (
          <GridContainer>
            <Grid size={{ md: 7, sm: 12, xs: 12 }}>
              {!existingAllocationId && !draftAllocationId && (
                <PageSection>
                  {!hasErrorInAllocation ? (
                    <Alert>
                      All shares have been successfully allocated. Please review
                      the information below and, if you wish to proceed, click
                      submit or save the allocation for future use (please note
                      you may only have one saved allocation per tranche at any
                      time).
                    </Alert>
                  ) : (
                    <Alert severity="error">
                      One or more of the allocation tranches require your
                      review. Please see the details below.
                    </Alert>
                  )}
                </PageSection>
              )}
            </Grid>
          </GridContainer>
        )}

        <CompanyDetails
          investee={investee}
          dealSharePrice={formFundAllocations[0]?.dealSharePrice}
          allocationDate={formFundAllocations[0]?.allocationDate}
          originalAllocationDate={fundAllocations[0]?.allocationDate}
          isEditing={isEditing}
          setValue={setValue}
          formFundAllocations={formFundAllocations}
          containsFrozenInvestments={formFundAllocations.some(
            (fundAllocation) =>
              fundAllocation.investments?.some(
                (investment) => investment.isFrozen,
              ) ?? false,
          )}
          isShareConsiderationAllocation={isShareConsiderationAllocation}
          exitedCompanyInfo={exitedCompanyInfo}
        />

        {uploadErrors.length > 0 ? (
          <PageSection>
            <ErrorTable errors={uploadErrors} />
          </PageSection>
        ) : uploadFile.isPending ? (
          <PageSection>
            <Row justify="center">
              <CircularProgress />
            </Row>
          </PageSection>
        ) : (
          <FundSection
            formFundAllocations={formFundAllocations}
            originalFundAllocations={fundAllocations}
            existingAllocationId={existingAllocationId}
            draftAllocationId={draftAllocationId}
            isEditing={isEditing}
            control={control}
            setValue={setValue}
            setIsEditing={setIsEditing}
            containsExitedShareholdings={containsExitedShareholdings}
            fundIdsInDraftAllocation={fundIdsInDraftAllocation}
            containsExportedTransactionFees={containsExportedTransactionFees}
            isShareConsiderationAllocation={isShareConsiderationAllocation}
            isAutoAllocation={isAutoAllocation}
            onReprocessAllocation={handleReprocessAllocation}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            amendmentsMenuAnchorEl={amendmentsMenuAnchorEl}
            onDownloadAllocationFile={() => {
              setUploadErrors([]);
              downloadAllocationFile(
                currentFormValues,
                investee?.tradingName ?? '',
                investee?.shareClass ?? '',
              );
            }}
            onFileInputChange={onFileInputChange}
            fileInputRef={fileInputRef}
            onDismissEdit={() => dismissEditDisclosure.onOpen()}
            onSaveEdit={handleSaveEdit}
            saveEditDisabled={
              existingAllocationId
                ? editShareAllocationsMutation.isPending ||
                  anyInvestorsOverAllocated
                : containsExitedShareholdings ||
                  containsExportedTransactionFees ||
                  anyInvestorsOverAllocated ||
                  !hasAnyInvestorsAllocated ||
                  createDraftAllocationMutation.isPending
            }
            saveEditDisabledTooltip={disabledSubmitTooltip}
            saveEditLoading={
              existingAllocationId
                ? editShareAllocationsMutation.isPending
                : createShareAllocationsMutation.isPending ||
                  editShareAllocationsMutation.isPending
            }
            hasAmendmentsFile={!!amendmentsFile}
            onFundModified={markFundAsModified}
          />
        )}

        {!existingAllocationId && (
          <PageSection>
            <Row justify="flex-end">
              <Typography
                variant="body2"
                color="textSecondary"
                fontWeight="bold"
                fontSize={12}
              >
                After submitting this allocation, all relevant investors will be
                able to view this company on their portal.
              </Typography>
            </Row>
          </PageSection>
        )}

        <PageSection>
          {!existingAllocationId ? (
            <ActionsRow
              hasUploadErrors={uploadErrors.length > 0}
              existingAllocationId={existingAllocationId}
              isEditing={isEditing}
              isAutoAllocation={isAutoAllocation}
              hasErrorInAllocation={hasErrorInAllocation}
              containsExitedShareholdings={containsExitedShareholdings}
              containsExportedTransactionFees={containsExportedTransactionFees}
              anyInvestorsOverAllocated={anyInvestorsOverAllocated}
              hasAnyInvestorsAllocated={hasAnyInvestorsAllocated}
              createDraftAllocationMutation={createDraftAllocationMutation}
              createShareAllocationsMutation={createShareAllocationsMutation}
              editShareAllocationsMutation={editShareAllocationsMutation}
              disabledSubmitTooltip={disabledSubmitTooltip}
              onReprocessAllocation={handleReprocessAllocation}
              onDismissEdit={() => dismissEditDisclosure.onOpen()}
              onDraftSubmit={handleSubmit(onDraftSubmit)}
              onSubmitClick={handleSubmitClick}
              onValidationAndSubmit={handleSubmit(handleValidationAndSubmit)}
              onDownloadAllocation={handleDownloadAllocation}
              hasAmendmentsFile={!!amendmentsFile}
              onReupload={handleReupload}
            />
          ) : (
            <Spacing size="sm">
              <ActionsRow
                hasUploadErrors={uploadErrors.length > 0}
                existingAllocationId={existingAllocationId}
                isEditing={isEditing}
                isAutoAllocation={isAutoAllocation}
                hasErrorInAllocation={hasErrorInAllocation}
                containsExitedShareholdings={containsExitedShareholdings}
                containsExportedTransactionFees={
                  containsExportedTransactionFees
                }
                anyInvestorsOverAllocated={anyInvestorsOverAllocated}
                hasAnyInvestorsAllocated={hasAnyInvestorsAllocated}
                createDraftAllocationMutation={createDraftAllocationMutation}
                createShareAllocationsMutation={createShareAllocationsMutation}
                editShareAllocationsMutation={editShareAllocationsMutation}
                disabledSubmitTooltip={disabledSubmitTooltip}
                onReprocessAllocation={handleReprocessAllocation}
                onDismissEdit={() => dismissEditDisclosure.onOpen()}
                onDraftSubmit={handleSubmit(onDraftSubmit)}
                onSubmitClick={handleSubmitClick}
                onValidationAndSubmit={handleSubmit(handleValidationAndSubmit)}
                onDownloadAllocation={handleDownloadAllocation}
                hasAmendmentsFile={!!amendmentsFile}
                onReupload={handleReupload}
              />
            </Spacing>
          )}
        </PageSection>
      </Spacing>

      {/* Upload warning modal */}
      <AlertDialog
        open={uploadWarningDisclosure.isOpen}
        onClose={uploadWarningDisclosure.onClose}
        title="Uploading an allocation edit"
        content={
          <p>
            Uploading an allocation edit will overwrite the existing
            auto-allocation results. This cannot be undone. Do you wish to
            proceed?
          </p>
        }
        btnLabels={{
          cancel: 'Go back',
          confirm: 'Proceed',
        }}
        cancelBtnProps={{
          variant: 'outlined',
          color: 'primary',
        }}
        confirmBtnProps={{
          variant: 'contained',
          color: 'primary',
        }}
        onConfirm={() => {
          uploadWarningDisclosure.onClose();
          setAmendmentsFile(null);
        }}
      />

      {/* Dismiss edit confirmation modal */}
      <AlertDialog
        open={dismissEditDisclosure.isOpen}
        onClose={dismissEditDisclosure.onClose}
        title="Clear all edits"
        content={
          <p>
            Do you wish to remove all edits and revert the allocation to its
            pre-edit state? This cannot be undone.
          </p>
        }
        btnLabels={{
          cancel: 'Go back',
          confirm: 'Clear edits',
        }}
        cancelBtnProps={{
          variant: 'outlined',
          color: 'primary',
        }}
        confirmBtnProps={{
          variant: 'outlined',
          color: 'secondary',
        }}
        onConfirm={handleDismissEdit}
      />

      {/* Allocation validation modal */}
      <AlertDialog
        open={allocationValidationDisclosure.isOpen}
        onClose={allocationValidationDisclosure.onClose}
        title="Allocation validation"
        content={<p>{getValidationMessage()}</p>}
        btnLabels={{
          cancel: 'Go back',
          confirm: 'Continue',
        }}
        cancelBtnProps={{
          variant: 'outlined',
          color: 'primary',
        }}
        confirmBtnProps={{
          variant: 'contained',
          color: 'primary',
        }}
        onConfirm={() => {
          allocationValidationDisclosure.onClose();
          handleSubmit(onSubmit)();
        }}
      />
    </PageContainer>
  );
};

export default ReviewSection;
